import React, { SyntheticEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { DownloadBottom as DownloadIcon } from '@videoblocks/react-icons';

import {
  selectHasVideoSubscription,
  selectIsLoggedIn,
} from '../../../../../../auth/AuthSelectors';
import { default as Tooltip } from '../../../../../../builder-io/storyblocks-design-system/components/atoms/Tooltip/Tooltip';
import { selectShowAnimatedAiThumbnailButton } from '../../../../selectors/searchSelectors';
import { VideoStockItemCardProps } from '../../VideoStockItemCard';

export interface DownloadButtonProps
  extends Pick<
    VideoStockItemCardProps,
    'shouldShowDownloadButton' | 'downloadButtonTelemetryAction'
  > {
  onClick: (evt: SyntheticEvent) => void;
}

export const DownloadButton = ({
  shouldShowDownloadButton,
  downloadButtonTelemetryAction,
  onClick,
}: DownloadButtonProps) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const videoSubscription = useSelector(selectHasVideoSubscription);
  const showAnimatedAiThumbnailButton = useSelector(
    selectShowAnimatedAiThumbnailButton
  );
  const isASubscriptionlessUser = isLoggedIn && !videoSubscription;

  if (shouldShowDownloadButton === false) return null;

  const buttonDivClassName = twMerge(
    'text-white bg-gray-900 w-10 h-10 rounded-full p-3 cursor-pointer hover:bg-opacity-90 flex items-center justify-center',
    !showAnimatedAiThumbnailButton && 'bg-opacity-80'
  );
  const svgClassName = 'fill-current w-4 h-4';

  return (
    <Tooltip
      open={showTooltip}
      title={isASubscriptionlessUser ? 'Download Preview' : 'Download'}
      trigger={
        <div
          tabIndex={0}
          className={buttonDivClassName}
          data-cy="download-button"
          onClick={(e) => {
            onClick(e);
            downloadButtonTelemetryAction?.();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClick(e);
              e.preventDefault();
            }
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onFocus={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onBlur={() => setShowTooltip(false)}
          aria-label="download"
          role="button"
        >
          <DownloadIcon
            data-testid="download-button-icon"
            className={svgClassName}
          />
        </div>
      }
    />
  );
};
